/**
 * Shared edit/delete confirm state for hub uploads (SubDetail + SubsTab + DocReview).
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  softDeleteHubUpload,
  fetchHubUploadDeletePreview,
  updateHubUploadDocType,
} from '../api/projects'
import type { HubInboxItem } from '../api/types'

export type HubDeleteCopy = {
  title: string
  body: string
  fileName: string
  subSentLine: string | null
}

function fallbackCopy(item: HubInboxItem, subDisplayName?: string | null): HubDeleteCopy {
  const name = item.original_name || 'this file'
  const byContractor = item.uploaded_by_role === 'contractor'
  const sub = subDisplayName || 'the sub'
  return {
    title: `Delete ${name}?`,
    body: "Removes it from the sub ledger and this project's Vault.",
    fileName: name,
    subSentLine: byContractor ? null : `This was sent by ${sub}.`,
  }
}

export function useHubUploadRowActions(projectId: number, subDisplayName?: string | null) {
  const qc = useQueryClient()
  const [editTarget, setEditTarget] = useState<HubInboxItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<HubInboxItem | null>(null)
  const [deleteCopy, setDeleteCopy] = useState<HubDeleteCopy | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['project-inbox', projectId] })
    void qc.invalidateQueries({ queryKey: ['project-vault', projectId] })
    void qc.invalidateQueries({ queryKey: ['sub-payments', projectId] })
  }

  async function openDelete(item: HubInboxItem) {
    setDeleteErr(null)
    setDeleteTarget(item)
    // Show dialog immediately (fallback) so delete never waits on the preview round-trip
    setDeleteCopy(fallbackCopy(item, subDisplayName))
    try {
      const copy = await fetchHubUploadDeletePreview(projectId, item.id)
      setDeleteCopy({
        title: copy.title,
        body: copy.body,
        fileName: copy.fileName || item.original_name || 'document',
        subSentLine: copy.subSentLine ?? null,
      })
    } catch {
      /* keep fallback */
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || deleteBusy) return
    setDeleteBusy(true)
    setDeleteErr(null)
    try {
      await softDeleteHubUpload(projectId, deleteTarget.id)
      setDeleteTarget(null)
      setDeleteCopy(null)
      invalidate()
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : 'Could not remove document')
    } finally {
      setDeleteBusy(false)
    }
  }

  async function saveEdit(
    docType: string,
    insuranceExpires: string | null,
    amount?: number | null,
  ) {
    if (!editTarget) return
    const body: {
      doc_type: string
      insurance_expires: string | null
      amount?: number | null
    } = {
      doc_type: docType,
      insurance_expires: insuranceExpires,
    }
    if (amount !== undefined) body.amount = amount
    await updateHubUploadDocType(projectId, editTarget.id, body)
    setEditTarget(null)
    invalidate()
  }

  return {
    editTarget,
    setEditTarget,
    deleteTarget,
    deleteCopy,
    deleteBusy,
    deleteErr,
    openDelete,
    confirmDelete,
    saveEdit,
    closeDelete: () => {
      if (!deleteBusy) {
        setDeleteTarget(null)
        setDeleteCopy(null)
      }
    },
  }
}
